import React, { useState } from 'react';
import { Customer, Order } from '../types';
import { Search, User, ShoppingBag, Calendar, Loader } from 'lucide-react';
import { formatPhoneNumber, formatDate } from '../utils';

interface CustomersProps {
  customers: Customer[];
  orders: Order[];
  onViewOrder: (order: Order) => void;
  loading?: boolean;
}

const Customers: React.FC<CustomersProps> = ({ customers, orders, onViewOrder, loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const filteredCustomers = customers.filter(c => {
      const search = searchTerm.toLowerCase();
      return (
          c.firstName.toLowerCase().includes(search) ||
          c.lastName.toLowerCase().includes(search) ||
          c.phone.includes(search)
      );
  });

  const getCustomerOrders = (customer: Customer) => {
      // First try to filter by customerId if available
      const customerOrders = orders.filter(o => 
          (o.customerId && o.customerId === customer.id) || 
          // Fall back to phone number for backward compatibility
          (!o.customerId && o.phone === customer.phone)
      );
      return customerOrders.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)] relative">
        {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
                <div className="flex flex-col items-center">
                    <Loader className="w-10 h-10 text-red-600 animate-spin mb-4" />
                    <p className="text-gray-700 font-medium">Loading customer data...</p>
                </div>
            </div>
        )}
        <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Customers</h2>
        </div>

        <div className="flex flex-1 gap-6 overflow-hidden">
            {/* List View */}
            <div className={`flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${selectedCustomer ? 'hidden md:flex' : ''}`}>
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input 
                            type="text" 
                            placeholder="Search name or phone..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                    </div>
                </div>
                <div className="overflow-y-auto flex-1 p-2 space-y-2">
                    {filteredCustomers.map(customer => (
                        <div 
                            key={customer.id}
                            onClick={() => setSelectedCustomer(customer)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${selectedCustomer?.id === customer.id ? 'bg-red-50 border-red-200 border' : 'hover:bg-gray-50 border border-transparent'}`}
                        >
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 mr-3">
                                    <User className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">{customer.lastName}, {customer.firstName}</p>
                                    <p className="text-xs text-gray-500">{formatPhoneNumber(customer.phone)}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-xs font-medium text-gray-600">
                                    {customer.totalOrders} Orders
                                </span>
                            </div>
                        </div>
                    ))}
                    {filteredCustomers.length === 0 && (
                        <div className="text-center py-10 text-gray-500">No customers found.</div>
                    )}
                </div>
            </div>

            {/* Detail View */}
            <div className={`flex-[2] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col ${!selectedCustomer ? 'hidden md:flex items-center justify-center bg-gray-50' : ''}`}>
                {!selectedCustomer ? (
                    <div className="text-gray-400 text-center">
                        <User className="w-16 h-16 mx-auto mb-2 opacity-20" />
                        <p>Select a customer to view history</p>
                    </div>
                ) : (
                    <>
                        <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-start">
                             <div>
                                <h3 className="text-2xl font-bold text-gray-900">{selectedCustomer.firstName} {selectedCustomer.lastName}</h3>
                                <p className="text-gray-600 font-mono mt-1">{formatPhoneNumber(selectedCustomer.phone)}</p>
                             </div>
                             <button 
                                onClick={() => setSelectedCustomer(null)}
                                className="md:hidden text-gray-500 underline text-sm"
                             >
                                Back to list
                             </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Order History</h4>
                            <div className="space-y-4">
                                {getCustomerOrders(selectedCustomer).map(order => (
                                    <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${order.status === 'Done' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {order.status}
                                                </span>
                                                <div className="flex items-center mt-2 text-sm text-gray-600">
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                    Pickup: {formatDate(order.pickupDate)}
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => onViewOrder(order)}
                                                className="text-red-600 text-sm hover:underline font-medium"
                                            >
                                                View / Edit
                                            </button>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                                            <p className="font-medium text-xs text-gray-500 mb-1">Items:</p>
                                            <ul className="list-disc pl-4 space-y-1">
                                                {order.items.map(i => (
                                                    <li key={i.id}>{i.quantity}x {i.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    </div>
  );
};

export default Customers;
